import {
  DEFAULT_SEARCH_FILTERS,
  SearchFilters,
} from '../types/searchFilters';

const ARRAY_KEYS: (keyof SearchFilters)[] = [
  'address_city',
  'address_district',
  'address_zone',
  'id_type',
  'qtd_rooms',
  'qtd_bathrooms',
  'qtd_suite',
  'qtd_parking',
];

const NUMBER_KEYS: (keyof SearchFilters)[] = [
  'min_sale_price',
  'max_sale_price',
  'min_rent_price',
  'max_rent_price',
  'min_taxas',
  'max_taxas',
  'min_area',
  'max_area',
];

function splitCsv(value: string | null): string[] {
  if (!value?.trim()) return [];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function shouldOmitDefault(
  key: keyof SearchFilters,
  value: SearchFilters[keyof SearchFilters],
): boolean {
  const defaultValue = DEFAULT_SEARCH_FILTERS[key];

  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    return value.length === 0;
  }

  if (typeof value === 'number' && typeof defaultValue === 'number') {
    return value === defaultValue;
  }

  if (key === 'order_by' && value === '0') return true;
  if (key === 'order_type' && value === 'desc') return true;

  return value === '' || value === defaultValue;
}

export function filtersToSearchParams(
  filters: SearchFilters,
  querySearch?: string,
): URLSearchParams {
  const params = new URLSearchParams();
  const merged: SearchFilters = {
    ...filters,
    query_search: querySearch ?? filters.query_search,
  };

  (Object.keys(merged) as (keyof SearchFilters)[]).forEach((key) => {
    const value = merged[key];
    if (shouldOmitDefault(key, value)) return;

    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
      return;
    }

    params.set(key, String(value));
  });

  return params;
}

export function searchParamsToFilters(
  params: URLSearchParams,
): { filters: SearchFilters; querySearch: string } {
  const filters: SearchFilters = { ...DEFAULT_SEARCH_FILTERS };

  const querySearch =
    params.get('query_search') ?? params.get('q') ?? '';

  filters.query_search = querySearch;

  const stringKeys: (keyof SearchFilters)[] = [
    'is_launch',
    'is_sale',
    'is_rent',
    'id_category',
    'id_empreendimento',
    'id_constructor',
    'address_street',
    'is_mobiliado',
    'order_by',
  ];

  stringKeys.forEach((key) => {
    const value = params.get(key);
    if (value !== null && value !== '') {
      (filters[key] as string) = value;
    }
  });

  ARRAY_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value !== null) {
      (filters[key] as string[]) = splitCsv(value);
    }
  });

  NUMBER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value !== null && value !== '' && !Number.isNaN(Number(value))) {
      (filters[key] as number) = Number(value);
    }
  });

  const orderType = params.get('order_type');
  if (orderType === 'asc' || orderType === 'desc') {
    filters.order_type = orderType;
  }

  if (!filters.order_by) {
    filters.order_by = '0';
  }

  return { filters, querySearch };
}
